import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, CheckCircle } from "lucide-react";
import { apiFetch } from "@/lib/api-fetch";

interface DemoFormProps {
  compact?: boolean;
}

export default function DemoForm({ compact = false }: DemoFormProps) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    facilitySize: "",
    operatorCount: "",
    currentMethod: "",
    preferredDate: "",
    preferredTime: "",
  });

  const update = (field: string, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setError("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.company) {
      setError("Please fill in all required fields.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const res = await apiFetch("/api/demo-request", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });

      const data = await res.json();
      if (data.success) {
        navigate("/thank-you");
      } else {
        setError(data.error || "Something went wrong. Please try again.");
      }
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Generate available dates (weekdays, 3+ days out)
  const getAvailableDates = () => {
    const dates: string[] = [];
    const today = new Date();
    let d = new Date(today);
    d.setDate(d.getDate() + 3);

    for (let i = 0; i < 20; i++) {
      const day = d.getDay();
      if (day !== 0 && day !== 6) {
        dates.push(d.toISOString().split("T")[0]);
      }
      d.setDate(d.getDate() + 1);
    }
    return dates;
  };

  const timeSlots = [
    "09:00", "09:30", "10:00", "10:30", "11:00", "11:30",
    "13:00", "13:30", "14:00", "14:30", "15:00", "15:30", "16:00", "16:30",
  ];

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr + "T12:00:00");
    return date.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
  };

  const formatTime = (time: string) => {
    const [h, m] = time.split(":");
    const hour = parseInt(h);
    const ampm = hour >= 12 ? "PM" : "AM";
    const h12 = hour > 12 ? hour - 12 : hour;
    return `${h12}:${m} ${ampm}`;
  };

  const inputClasses = "w-full px-4 py-3 bg-secondary border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-forest-600 focus:border-transparent text-sm";
  const selectClasses = "w-full px-4 py-3 bg-secondary border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-forest-600 focus:border-transparent text-sm appearance-none";
  const labelClasses = "block text-sm font-medium text-foreground mb-1.5";

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* Required fields */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className={labelClasses}>Full Name *</label>
          <input
            type="text"
            placeholder="John Smith"
            value={form.name}
            onChange={(e) => update("name", e.target.value)}
            className={inputClasses}
            required
          />
        </div>
        <div>
          <label className={labelClasses}>Work Email *</label>
          <input
            type="email"
            placeholder="john@facility.com"
            value={form.email}
            onChange={(e) => update("email", e.target.value)}
            className={inputClasses}
            required
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className={labelClasses}>Company Name *</label>
          <input
            type="text"
            placeholder="Acme Composting"
            value={form.company}
            onChange={(e) => update("company", e.target.value)}
            className={inputClasses}
            required
          />
        </div>
        <div>
          <label className={labelClasses}>Phone</label>
          <input
            type="tel"
            placeholder="(555) 123-4567"
            value={form.phone}
            onChange={(e) => update("phone", e.target.value)}
            className={inputClasses}
          />
        </div>
      </div>

      {/* Qualifying fields */}
      {!compact && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className={labelClasses}>Facility Size</label>
              <select
                value={form.facilitySize}
                onChange={(e) => update("facilitySize", e.target.value)}
                className={selectClasses}
              >
                <option value="">Select size</option>
                <option value="small">Small (&lt; 5 acres)</option>
                <option value="medium">Medium (5-20 acres)</option>
                <option value="large">Large (20-100 acres)</option>
                <option value="enterprise">Enterprise (100+ acres)</option>
              </select>
            </div>
            <div>
              <label className={labelClasses}>Number of Operators</label>
              <select
                value={form.operatorCount}
                onChange={(e) => update("operatorCount", e.target.value)}
                className={selectClasses}
              >
                <option value="">Select range</option>
                <option value="1-5">1-5</option>
                <option value="6-15">6-15</option>
                <option value="16-50">16-50</option>
                <option value="50+">50+</option>
              </select>
            </div>
            <div>
              <label className={labelClasses}>Current Tracking Method</label>
              <select
                value={form.currentMethod}
                onChange={(e) => update("currentMethod", e.target.value)}
                className={selectClasses}
              >
                <option value="">Select method</option>
                <option value="paper">Paper / Clipboards</option>
                <option value="spreadsheets">Spreadsheets</option>
                <option value="other_software">Other Software</option>
                <option value="none">No Formal Tracking</option>
              </select>
            </div>
          </div>

          {/* Date/Time preference */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClasses}>Preferred Demo Date</label>
              <select
                value={form.preferredDate}
                onChange={(e) => update("preferredDate", e.target.value)}
                className={selectClasses}
              >
                <option value="">Select a date</option>
                {getAvailableDates().map((d) => (
                  <option key={d} value={d}>
                    {formatDate(d)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelClasses}>Preferred Time (AZ Time)</label>
              <select
                value={form.preferredTime}
                onChange={(e) => update("preferredTime", e.target.value)}
                className={selectClasses}
              >
                <option value="">Select a time</option>
                {timeSlots.map((t) => (
                  <option key={t} value={t}>
                    {formatTime(t)}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </>
      )}

      {error && (
        <div className="text-red-400 text-sm bg-red-950/30 border border-red-900/50 rounded-lg px-4 py-3">
          {error}
        </div>
      )}

      <button
        type="submit"
        disabled={loading}
        className="w-full flex items-center justify-center gap-2 px-6 py-3.5 bg-forest-600 hover:bg-forest-700 disabled:opacity-50 text-white font-semibold rounded-lg transition-colors text-sm"
      >
        {loading ? (
          <>
            <Loader2 className="animate-spin" size={18} />
            Submitting...
          </>
        ) : (
          "Request a Demo"
        )}
      </button>

      <p className="text-xs text-muted-foreground text-center">
        No commitment required. We'll reach out within one business day.
      </p>
    </form>
  );
}
